import { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TankCanvas, { TankCanvasHandle } from './TankCanvas';

const HeroSection = () => {
  const { t } = useTranslation();
  const heroRef       = useRef<HTMLElement>(null);
  const tankCanvasRef = useRef<TankCanvasHandle>(null);
  const pivotRef      = useRef<HTMLDivElement>(null);

  const [cursor, setCursor] = useState({ x: -999, y: -999, visible: false });

  const getAngle = useCallback((mx: number, my: number) => {
    if (!pivotRef.current) return 0;
    const r  = pivotRef.current.getBoundingClientRect();
    const cx = r.left + r.width  / 2;
    const cy = r.top  + r.height / 2;
    return Math.atan2(my - cy, mx - cx) * (180 / Math.PI);
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const onMove = (e: MouseEvent) => {
      tankCanvasRef.current?.setAngle(getAngle(e.clientX, e.clientY));
      setCursor({ x: e.clientX, y: e.clientY, visible: true });
    };
    const onLeave = () => setCursor(c => ({ ...c, visible: false }));

    hero.addEventListener('mousemove',  onMove);
    hero.addEventListener('mouseleave', onLeave);
    return () => {
      hero.removeEventListener('mousemove',  onMove);
      hero.removeEventListener('mouseleave', onLeave);
    };
  }, [getAngle]);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden select-none"
      style={{ cursor: 'none' }}
    >
      {/* Кастомный прицел */}
      {cursor.visible && (
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{ left: cursor.x, top: cursor.y, transform: 'translate(-50%,-50%)' }}
        >
          <div className="absolute rounded-full border border-[#d4af37]/80"
            style={{ width: 36, height: 36, left: -18, top: -18 }} />
          <div className="absolute rounded-full border border-[#d4af37]/50"
            style={{ width: 18, height: 18, left: -9, top: -9 }} />
          <div className="absolute rounded-full bg-[#d4af37]"
            style={{ width: 4, height: 4, left: -2, top: -2 }} />
          <div className="absolute bg-[#d4af37]/80"
            style={{ width: 28, height: 1, left: -14, top: -0.5 }} />
          <div className="absolute bg-[#d4af37]/80"
            style={{ width: 1, height: 28, left: -0.5, top: -14 }} />
        </div>
      )}

      {/* Фон */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/56137822a75f08584b3ae858edde0b1f.jpg"
          alt="background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-24 flex flex-col lg:flex-row items-center gap-8 lg:gap-16">

        {/* ЛЕВЫЙ ТЕКСТ */}
        <div className="flex-1 w-full order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 bg-[#d4af37]/20 border border-[#d4af37]/40 rounded-full px-4 py-1.5 mb-5">
            <div className="w-2 h-2 bg-[#d4af37] rounded-full animate-pulse" />
            <span className="text-[#d4af37] text-xs tracking-widest uppercase font-medium">{t('hero.badge')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white leading-tight mb-5 uppercase" style={{ fontFamily: "'Oswald',sans-serif" }}>
            {t('hero.title1')}<br />
            <span className="text-[#d4af37]">{t('hero.title2')}</span><br />
            {t('hero.title3')}
          </h1>
          <p className="text-white/70 text-base md:text-lg mb-6 max-w-xl leading-relaxed" style={{ fontFamily: "'Inter',sans-serif" }}>
            {t('hero.description', { return: '37%' })}
          </p>
          <div className="flex flex-col gap-2.5 mb-8">
            {[
              { icon: 'ri-bar-chart-fill',       text: t('hero.stat1') },
              { icon: 'ri-shield-check-fill',    text: t('hero.stat2') },
              { icon: 'ri-arrow-up-circle-fill', text: t('hero.stat3') },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-[#d4af37] text-lg`} />
                </div>
                <span className="text-white/85 text-sm tracking-wide" style={{ fontFamily: "'Inter',sans-serif" }}>{item.text}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#d4af37] text-black font-bold px-6 py-3.5 rounded-full hover:bg-[#e8c84a] transition-all duration-200 whitespace-nowrap text-sm tracking-widest uppercase cursor-pointer"
              style={{ fontFamily: "'Inter',sans-serif" }}
            >{t('hero.cta1')}</button>
            <button
              onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
              className="border border-white/40 text-white font-medium px-6 py-3.5 rounded-full hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-200 whitespace-nowrap text-sm tracking-widest uppercase cursor-pointer"
              style={{ fontFamily: "'Inter',sans-serif" }}
            >{t('hero.cta2')}</button>
          </div>
        </div>

        {/* ПРАВЫЙ БЛОК: БАШНЯ */}
        <div className="flex-shrink-0 flex flex-col items-center gap-4 order-1 lg:order-2">
          <div className="relative w-[260px] h-[260px] sm:w-[360px] sm:h-[360px] lg:w-[560px] lg:h-[560px]">
            {/* Точка поворота башни */}
            <div
              ref={pivotRef}
              className="absolute pointer-events-none"
              style={{ left: '50%', top: '50%', width: 1, height: 1 }}
            />
            {/* Свечение под башней */}
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{
              background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.15) 0%, transparent 70%)',
            }} />

            <div className="w-full h-full">
              <TankCanvas
                ref={tankCanvasRef}
                width={640}
                height={640}
              />
            </div>

            {/* Подпись */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-black/65 border border-[#d4af37]/30 rounded-lg px-3 py-1.5 backdrop-blur-sm whitespace-nowrap pointer-events-none">
              <div className="text-[#d4af37] text-xs tracking-widest uppercase text-center mb-0.5">{t('hero.turretLabel')}</div>
              <div className="text-white/60 text-xs text-center hidden sm:block">{t('hero.turretHint')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce pointer-events-none">
        <span className="text-white/40 text-xs tracking-widest uppercase">{t('hero.scroll')}</span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/40 text-lg" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
